import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateRangePicker, createStaticRanges } from 'react-date-range';
import { format, subDays, subMonths, startOfDay, endOfDay } from 'date-fns';
import { API_BASE, apiFetch } from '../lib/api';
import { toast } from 'react-toastify';
import { FaCalendarAlt, FaTimes } from 'react-icons/fa';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

type OrderStatus = 'draft' | 'punched' | 'cancelled' | 'failed';

type DealerOrder = {
    id: number;
    mother_key: string;
    product: string;
    quantity: string;
    unit: string;
    scheme_name: string | null;
    remarks: string | null;
    status: OrderStatus;
    crm_ref: string | null;
    channel: string;
    created_at: string;
    punched_at: string | null;
};

const STATUS_STYLES: Record<OrderStatus, string> = {
    draft:     'bg-yellow-100 text-yellow-700',
    punched:   'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-600',
    failed:    'bg-gray-100 text-gray-500',
};

const ALL_STATUSES: OrderStatus[] = ['draft', 'punched', 'cancelled', 'failed'];

const STATIC_RANGES = createStaticRanges([
    {
        label: 'Last 7 days',
        range: () => ({ startDate: subDays(new Date(), 6), endDate: new Date() }),
    },
    {
        label: 'Last 30 days',
        range: () => ({ startDate: subDays(new Date(), 29), endDate: new Date() }),
    },
    {
        label: 'Last 3 months',
        range: () => ({ startDate: subMonths(new Date(), 3), endDate: new Date() }),
    },
]);

const DealerOrders: React.FC = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState<DealerOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>('');

    const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
        startDate: null,
        endDate: null,
    });
    const [pickerOpen, setPickerOpen] = useState(false);
    const pickerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onClickOutside = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
                setPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter)        params.set('status', statusFilter);
            if (dateRange.startDate) params.set('from', format(dateRange.startDate, 'yyyy-MM-dd'));
            if (dateRange.endDate)   params.set('to',   format(dateRange.endDate,   'yyyy-MM-dd'));

            const url = `${API_BASE}/api/dealer-orders${params.toString() ? '?' + params.toString() : ''}`;
            const res = await apiFetch(url);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                toast.error(data?.error ?? 'Failed to load dealer orders');
                return;
            }
            setRows(data.rows ?? []);
        } catch {
            toast.error('Failed to load dealer orders');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [statusFilter, dateRange]);

    const clearDates = () => setDateRange({ startDate: null, endDate: null });

    const dateLabel = () => {
        const { startDate, endDate } = dateRange;
        if (!startDate && !endDate) return 'Date range';
        if (startDate && endDate)
            return `${format(startDate, 'dd MMM yy')} – ${format(endDate, 'dd MMM yy')}`;
        if (startDate) return `From ${format(startDate, 'dd MMM yy')}`;
        return 'Date range';
    };

    const filtered = filter.trim()
        ? rows.filter(r =>
            r.mother_key.toLowerCase().includes(filter.toLowerCase()) ||
            r.product.toLowerCase().includes(filter.toLowerCase()) ||
            (r.scheme_name ?? '').toLowerCase().includes(filter.toLowerCase()) ||
            (r.crm_ref ?? '').toLowerCase().includes(filter.toLowerCase())
        )
        : rows;

    const hasDateFilter = !!dateRange.startDate || !!dateRange.endDate;

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Dealer Orders</h1>
                    <p className="text-sm text-gray-500 mt-0.5">All orders captured by the AI — latest first</p>
                </div>
                <span className="text-sm font-medium text-gray-600 bg-gray-200 px-3 py-1 rounded-full">
                    {filtered.length} {statusFilter || 'total'}
                </span>
            </div>

            {/* Filters row */}
            <div className="flex flex-wrap items-center gap-3 mb-4 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
                {/* Text search */}
                <input
                    type="text"
                    placeholder="Filter by dealer, product, scheme, CRM ref…"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    className="w-full max-w-sm border border-gray-300 rounded-lg px-4 py-2.5 text-sm bg-white shadow-md focus:outline-none focus:border-gray-500 transition-colors"
                />

                {/* Date range picker */}
                <div className="relative" ref={pickerRef}>
                    <button
                        onClick={() => setPickerOpen(o => !o)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm border shadow-md transition-colors cursor-pointer
                            ${hasDateFilter
                                ? 'bg-gray-700 text-white border-gray-700'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                    >
                        <FaCalendarAlt size={13} />
                        {dateLabel()}
                    </button>
                    {hasDateFilter && (
                        <button
                            onClick={clearDates}
                            className="absolute -top-1.5 -right-1.5 bg-gray-500 text-white rounded-full w-4 h-4 flex items-center justify-center hover:bg-red-500 transition-colors cursor-pointer"
                            title="Clear dates"
                        >
                            <FaTimes size={8} />
                        </button>
                    )}
                    {pickerOpen && (
                        <div className="absolute z-50 mt-2 shadow-xl rounded-lg overflow-hidden border border-gray-200">
                            <DateRangePicker
                                ranges={[{
                                    startDate: dateRange.startDate ?? new Date(),
                                    endDate:   dateRange.endDate   ?? new Date(),
                                    key: 'selection',
                                }]}
                                onChange={item => {
                                    const sel = item.selection;
                                    const start = sel.startDate ?? null;
                                    const end   = sel.endDate   ?? null;
                                    setDateRange({ startDate: start, endDate: end });
                                    if (start && end && start.getTime() !== end.getTime()) {
                                        setPickerOpen(false);
                                    }
                                }}
                                staticRanges={STATIC_RANGES}
                                inputRanges={[]}
                                moveRangeOnFirstSelection={false}
                                maxDate={new Date()}
                                rangeColors={['#374151']}
                                months={2}
                                direction="horizontal"
                            />
                        </div>
                    )}
                </div>

                {/* Status tab selector */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1 border border-gray-200">
                    {(['', ...ALL_STATUSES] as (OrderStatus | '')[]).map(s => (
                        <button
                            key={s || 'all'}
                            onClick={() => setStatusFilter(s)}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-all cursor-pointer
                                ${statusFilter === s
                                    ? 'bg-white text-gray-800 shadow-sm border border-gray-200'
                                    : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {s || 'All'}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <p className="text-sm text-gray-500">Loading…</p>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-lg">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-700 text-white">
                            <tr>
                                <th className="px-4 py-3 text-left font-medium">Dealer</th>
                                <th className="px-4 py-3 text-left font-medium">Product</th>
                                <th className="px-4 py-3 text-left font-medium">Qty</th>
                                <th className="px-4 py-3 text-left font-medium">Scheme</th>
                                <th className="px-4 py-3 text-left font-medium">Remarks</th>
                                <th className="px-4 py-3 text-left font-medium">Status</th>
                                <th className="px-4 py-3 text-left font-medium">CRM Ref</th>
                                <th className="px-4 py-3 text-left font-medium">Channel</th>
                                <th className="px-4 py-3 text-left font-medium">Date</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-400">
                                        No orders found.
                                    </td>
                                </tr>
                            ) : filtered.map((row, i) => (
                                <tr key={row.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <button
                                            onClick={() => navigate(`/chat?distributor=${encodeURIComponent(row.mother_key)}`)}
                                            className="font-medium text-gray-800 hover:text-blue-600 hover:underline cursor-pointer transition-colors"
                                        >
                                            {row.mother_key}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-gray-700">{row.product}</td>
                                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{Number(row.quantity).toLocaleString()} {row.unit}</td>
                                    <td className="px-4 py-3 text-gray-600">{row.scheme_name ?? <span className="text-gray-300">—</span>}</td>
                                    <td className="px-4 py-3 text-gray-600 max-w-xs">
                                        {row.remarks
                                            ? <p className="text-xs line-clamp-2">{row.remarks}</p>
                                            : <span className="text-gray-300">—</span>
                                        }
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium capitalize ${STATUS_STYLES[row.status]}`}>
                                            {row.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{row.crm_ref ?? <span className="text-gray-300">—</span>}</td>
                                    <td className="px-4 py-3 text-gray-600 capitalize whitespace-nowrap">{row.channel}</td>
                                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                                        {new Date(row.created_at).toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default DealerOrders;
